import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { usePlayPageData } from "@/hooks/usePlayPageData";
import { useGetActiveChallenges } from "@/hooks/challenges/useGetActiveChallenges";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import ProgressBar from "@ramonak/react-progress-bar";
import "./PlayCockpitV2.scss";

/**
 * Compact Gaming Header - Matches Your Screenshot Design
 * Horizontal layout with real data integration
 */
const CompactGamingHeader: React.FC<{
    fuelBalance: string;
    accountLevel: any;
    currentUser: any;
    gameInfo: any;
}> = ({ fuelBalance, accountLevel, currentUser, gameInfo }) => {
    // Calculate XP percentage using REAL data structure
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || 1;
    const xpPercentage = experienceGained / (experienceGained + experienceRequired) * 100;
    const userLevel = accountLevel?.level || 1;

    // Mock data for features not yet implemented (TODO: Connect to real data)
    const streakCount = 7;
    const lastRewardAmount = "500 FUEL";
    const lastRewardTime = "2h ago";
    const multiplierValue = "2X";
    const multiplierTimer = "23:45:12";
    const currentGame = gameInfo?.title || "Valorant"; // Use real game or fallback

    return (
        <div className="compact-gaming-header">
            {/* Level Badge */}
            <div className="level-badge">
                <div className="level-badge__star">⭐</div>
                <span className="level-badge__number">{userLevel}</span>
            </div>

            {/* XP Progress */}
            <div className="xp-progress">
                <span className="xp-progress__label">XP TO NEXT</span>
                <div className="xp-progress__bar">
                    <div
                        className="xp-progress__fill"
                        style={{ width: `${Math.min(xpPercentage, 100)}%` }}
                    />
                </div>
                <span className="xp-progress__amount">
                    {experienceRequired.toLocaleString()}
                </span>
            </div>

            {/* Game Status */}
            <div className="game-status">
                <div className="game-status__icon">🎮</div>
                <span className="game-status__name">{currentGame}</span>
                <div className="game-status__dot" />
            </div>

            {/* Last Reward */}
            <div className="last-reward">
                <div className="last-reward__icon">🎁</div>
                <span className="last-reward__text">Last Reward: {lastRewardAmount}</span>
                <span className="last-reward__time">{lastRewardTime}</span>
            </div>

            {/* Multiplier */}
            <div className="multiplier">
                <div className="multiplier__icon">⚡</div>
                <span className="multiplier__value">{multiplierValue} MULTIPLIER</span>
                <span className="multiplier__timer">{multiplierTimer}</span>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
                <button className="quick-action quick-action--fuel">
                    💡
                </button>
                <button className="quick-action quick-action--alert">
                    ❓
                </button>
                <button className="quick-action quick-action--profile">
                    <img
                        src={currentUser?.user?.gravatar || "/placeholder-avatar.jpg"}
                        alt="Profile"
                    />
                </button>
            </div>
        </div>
    );
};

/**
 * PlayCockpitV2 - Compact Gaming Header with REAL Data
 * Matches the design from your screenshot
 */
const PlayCockpitV2: React.FC = () => {
    // Get REAL data from Redux and hooks
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const accountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);
    const gameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const { fuelBalance } = usePlayPageData();
    const { data: userProfile } = useGetUserProfile(fullToken);

    return (
        <div className="cockpit-v2">
            {/* Compact Gaming Header - This is the main attraction */}
            <CompactGamingHeader
                fuelBalance={fuelBalance}
                accountLevel={accountLevel}
                currentUser={userProfile || currentUser}
                gameInfo={gameInfo}
            />

            {/* Rest of the page - for now show we're building */}
            <div className="cockpit-v2__content">
                <div className="cockpit-v2__demo">
                    <h2>🎮 Gaming Header V2 - LIVE DATA</h2>
                    <div className="data-display">
                        <div className="data-item">
                            <strong>User Level:</strong> {accountLevel?.level || "Loading..."}
                        </div>
                        <div className="data-item">
                            <strong>XP Progress:</strong> {accountLevel?.experienceGainedAtThisLevel || 0} / {(accountLevel?.experienceGainedAtThisLevel || 0) + (accountLevel?.experienceRequiredForNextLevel || 1)}
                        </div>
                        <div className="data-item">
                            <strong>Fuel Balance:</strong> {fuelBalance}
                        </div>
                        <div className="data-item">
                            <strong>Current Game:</strong> {gameInfo?.title || "No game detected"}
                        </div>
                        <div className="data-item">
                            <strong>User Avatar:</strong> {userProfile?.user?.gravatar ? "✅ Loaded" : "❌ Missing"}
                        </div>
                    </div>
                    <p className="note">👆 This shows the header is connected to REAL data!</p>
                </div>
            </div>
        </div>
    );
};

export default PlayCockpitV2;